<template>
  <div>
    <div class="relative overflow-x-auto shadow-md sm:rounded-sm mt-5">
      <table class="w-full z-10 text-left rtl:text-right text-gray-700">
        <thead>
          <tr class="bg-gray-100">
            <th scope="col" class="px-6 py-3"><div class="flex items-center">#</div></th>
            <th scope="col" class="px-6"><div class="flex items-center">Batch No</div></th>
            <th scope="col" class="px-6"><div class="flex items-center">Expiry Date</div></th>
            <th scope="col" class="px-6"><div class="flex items-center">Available Quantity</div></th>
            <th scope="col" class="px-6"><div class="flex items-center">Actual Quantity</div></th>
            <th scope="col" class="px-6"><div class="flex items-center">Cost Price</div></th>
            <th scope="col" class="px-6"><div class="flex items-center">Selling Price</div></th>
            <th scope="col" class="px-6">Added On</th>
            <th scope="col" class="px-6">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(stock_item, key) in paginatedData" :key="key" class="border-b">
            <td class="px-6 py-2">{{ key + 1 }}</td>
            <td class="px-6 py-2">{{ stock_item.batch_no }}</td>
            <td class="px-6 py-2">{{ stock_item.expiry_date }}</td>
            <td class="px-6 py-2">{{ stock_item.available_quantity }}</td>
            <td class="px-6 py-2">{{ stock_item.actual_quantity }}</td>
            <td class="px-6 py-2">{{ stock_item.cost_price }}</td>
            <td class="px-6 py-2">{{ stock_item.selling_price }}</td>
            <td class="px-6 py-2">{{ stock_item.created_at }}</td>
            <td class="px-6 py-2">
              <div class="flex space-x-2">
                <button
                  @click="openEditModal(stock_item)"
                  class="btn-xs bg-custom-blue text-white px-4 py-1 rounded flex items-center gap-1"
                >
                  <Edit color="white" :size="18" />
                </button>
                <button
                  @click="openDeleteModal(stock_item)"
                  class="btn-xs bg-red-500 text-white px-4 py-1 rounded flex items-center gap-1"
                >
                  <Trash2 color="white" :size="18" />
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div class="flex justify-center gap-4 mt-4">
      <div class="flex items-center gap-2">
        <button :disabled="currentPage === 1" @click="previousPage"
          class="flex items-center gap-2 px-6 py-3 font-bold text-custom-blue align-middle transition-all rounded-lg">
          Previous
        </button>
        <div class="flex items-center gap-2">
          <button v-for="page in totalPages" :key="page"
            :class="{ 'bg-custom-blue text-white': currentPage === page, 'text-custom-blue': currentPage !== page }"
            @click="goToPage(page)"
            class="relative h-10 w-10 max-w-[40px] rounded-lg text-center align-middle font-medium transition-all">
            {{ page }}
          </button>
        </div>
        <button :disabled="currentPage === totalPages" @click="nextPage"
          class="flex items-center gap-2 px-6 py-3 font-bold text-custom-blue align-middle transition-all rounded-lg">
          Next
        </button>
      </div>
    </div>

    <!-- Edit Modal -->
    <EditInventoryStockModal
      :show="showEditModal"
      :stockItem="stockItemToEdit"
      @close="showEditModal = false"
      @updated="$emit('refresh')"
    />

    <!-- Delete Confirmation Modal -->
    <ConfirmationModal
      :show="showDeleteModal"
      :showLoader="deleteLoader"
      title="Delete Stock Batch"
      message="Are you sure you want to delete this stock batch? This will deduct its quantities from the store total and cannot be undone."
      @cancel="showDeleteModal = false"
      @confirm="confirmDelete"
    />
  </div>
</template>

<script>
import { Edit, Trash2 } from "lucide-vue-next";
import EditInventoryStockModal from "@/views/modals/inventory/EditInventoryStockModal.vue";
import ConfirmationModal from "@/views/modals/shared/ConfirmationModal.vue";
import apiClient from "@/services/api";

export default {
  name: "InventoryItemStockDetailsTab",
  components: {
    Edit,
    Trash2,
    EditInventoryStockModal,
    ConfirmationModal,
  },
  emits: ["refresh"],
  props: {
    searchQuery: { type: String, default: "" },
    inventory_store_items: { type: Array, required: true },
  },
  data() {
    return {
      currentPage: 1,
      itemsPerPage: 10,
      showEditModal: false,
      showDeleteModal: false,
      deleteLoader: false,
      stockItemToEdit: {},
      stockItemToDelete: null,
    };
  },
  computed: {
    inventory_store_stock_items() {
      if (!this.inventory_store_items.length) return [];
      return this.inventory_store_items.flatMap(
        (item) => item.inventory_store_stock_items
      );
    },
    totalPages() {
      if (Array.isArray(this.inventory_store_stock_items) && this.inventory_store_stock_items.length > 0 && this.itemsPerPage > 0) {
        return Math.ceil(this.inventory_store_stock_items.length / this.itemsPerPage);
      }
      return 0;
    },
    paginatedData() {
      const start = (Number(this.currentPage) - 1) * Number(this.itemsPerPage);
      return this.inventory_store_stock_items.slice(start, start + Number(this.itemsPerPage));
    },
  },
  methods: {
    openEditModal(stockItem) {
      this.stockItemToEdit = stockItem;
      this.showEditModal = true;
    },
    openDeleteModal(stockItem) {
      this.stockItemToDelete = stockItem;
      this.showDeleteModal = true;
    },
    async confirmDelete() {
      if (!this.stockItemToDelete) return;
      try {
        this.deleteLoader = true;
        const response = await apiClient.deleteInventoryStockItem(this.stockItemToDelete.id);
        if (response.success) {
          this.$toast.success("Stock batch deleted successfully");
          this.showDeleteModal = false;
          this.stockItemToDelete = null;
          this.$emit("refresh"); // tell parent to reload item details
        } else {
          this.$toast.error(response.message || "Failed to delete stock batch");
        }
      } catch (error) {
        this.$toast.error("Failed to delete stock batch");
        console.log(error);
      } finally {
        this.deleteLoader = false;
      }
    },
    goToPage(pageNumber) { this.currentPage = pageNumber; },
    nextPage() { if (this.currentPage < this.totalPages) this.currentPage++; },
    previousPage() { if (this.currentPage > 1) this.currentPage--; },
  },
};
</script>